import { create } from "zustand";
import { persist } from "zustand/middleware";
import api from "../services/api";

/**
 * ⭐ useAuthStore - Global authentication state
 *
 * State:
 *  - user: Current logged-in user or null
 *  - isLoading: Request in progress (login/register/logout)
 *  - error: Error message from last failed operation
 *  - isHydrated: Flag to prevent flash logout (important!)
 *
 * Actions:
 *  - setUser: Set user directly (used by hydration)
 *  - login: Async login with email/password
 *  - register: Async register with email/password
 *  - logout: Clear user and error state
 *  - clearError: Clear error message
 *  - setHydrated: Mark store as hydrated (after localStorage load)
 */

const useAuthStore = create(
  persist(
    (set) => ({
      // ============ STATE ============
      user: null,
      isLoading: false,
      error: null,
      isHydrated: false, // ⭐ Prevents flash logout on page reload

      // ============ ACTIONS ============

      /**
       * Set user directly (used by hydration)
       * @param {Object|null} user - User object with _id, email, role, etc.
       */
      setUser: (user) => set({ user }),

      /**
       * Clear error message
       */
      clearError: () => set({ error: null }),

      /**
       * Login user with email and password
       * @param {String} email
       * @param {String} password
       * @returns {Promise<Object>} User data if success, throws error if fail
       */
      login: async (email, password) => {
        set({ isLoading: true, error: null });
        try {
          const response = await api.post("/api/login", {
            email,
            password,
          });

          const userData = response.data.user || response.data.data;
          const token = response.data.token;

          if (token) {
            localStorage.setItem("token", token);
          }

          set({
            user: userData,
            isLoading: false,
          });
          return userData;
        } catch (error) {
          const errorMsg =
            error.response?.data?.message || error.message || "Login failed";
          set({
            error: errorMsg,
            isLoading: false,
          });
          throw error;
        }
      },

      /**
       * Register new user with email and password
       * @param {String} email
       * @param {String} password
       * @param {String} name
       * @returns {Promise<Object>} User data if success
       */
      register: async (email, password, name) => {
        set({ isLoading: true, error: null });
        try {
          const response = await api.post("/api/register", {
            email,
            password,
            name,
          });

          const userData = response.data.user || response.data.data;
          const token = response.data.token;

          if (token) {
            localStorage.setItem("token", token);
          }

          set({
            user: userData,
            isLoading: false,
          });
          return userData;
        } catch (error) {
          const errorMsg =
            error.response?.data?.message ||
            error.message ||
            "Registration failed";
          set({
            error: errorMsg,
            isLoading: false,
          });
          throw error;
        }
      },

      /**
       * Logout user
       */
      logout: () => {
        localStorage.removeItem("token");
        localStorage.removeItem("user");
        set({
          user: null,
          error: null,
          isLoading: false,
        });
      },

      /**
       * Mark store as hydrated (call this after localStorage is loaded)
       * This prevents the "flash logout" issue
       * ⭐ IMPORTANT: Call this in App.jsx useEffect!
       */
      setHydrated: () => set({ isHydrated: true }),
    }),
    {
      name: "auth-storage", // localStorage key
      partialize: (state) => ({
        user: state.user, // Only persist user, not isLoading/error
      }),
      // Note: isHydrated is NOT persisted - resets on each page load
    },
  ),
);

export default useAuthStore;
